import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { useLocale } from '~/hooks/useLocale';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

import { type TimeRange } from './useSchoolBehaviorReportQuery';

const log = createLogger('useStudentSummaryQuery');

export const STUDENT_SUMMARY_QUERY_KEY = 'studentSummary';

export type StudentSummaryResponse = {
  summary: string;
  sessions: number;
  praises: number;
  warnings: number;
  absences: number;
};

export function useStudentSummaryQuery({
  periodId,
  studentId,
  timeRange,
  enabled = false,
}: {
  periodId: string;
  studentId: string;
  timeRange: TimeRange;
  enabled?: boolean;
}) {
  const httpClient = useHttpClient();
  const locale = useLocale();

  const { data, error, status, refetch } = useQuery<StudentSummaryResponse>({
    queryKey: [STUDENT_SUMMARY_QUERY_KEY, periodId, studentId, timeRange],
    queryFn: async () => {
      const tz = locale?.timeZone ?? '';
      log.debug('Fetching student summary', { periodId, studentId, timeRange, tz });
      const params = new URLSearchParams({ periodId, studentId, range: timeRange, tz });
      const response = await httpClient.get<StudentSummaryResponse>(`/report/getSchoolStudentSummary?${params}`);
      log.info('Fetched student summary successfully', {
        summaryLength: response.summary.length,
        sessions: response.sessions,
      });
      return response;
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch student summary', failureCount, error, log }),
    enabled: httpClient.isAuthenticated && !!periodId && !!studentId && enabled,
  });

  return { data, error, status, refetch };
}
